import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Grid,
} from '@mui/material';
import {
  Support as SupportIcon,
  Close as CloseIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material';
import { ticketService } from '../../services/ticketService';
import { ContratoServicos, Client, Ticket } from '../../types';
import { toast } from 'react-toastify';
import ClientSelect from '../selects/ClientSelect';
import ContractServicosSelect from '../selects/ContractServicosSelect';
import ChamadoAssuntoSelect from '../selects/ChamadoAssuntoSelect';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import SysDateTime from '../SysDateTime';

interface ChamadoFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  clientId?: number; // Agora é opcional, pode ser selecionado no formulário
  ticketId?: number | null; // Id do chamado para edição
}

interface ChamadoFormData {
  chamado_assunto_id: number | null;
  descricao: string;
  prioridade: 'baixa' | 'normal' | 'alta' | 'urgente';
  quem_solicitou: string;
  contrato_id: number | '';
  contrato_servico_id: number | '';
  data_agendamento: Date | null;
  atribuido_para: number | null;
}

const PRIORIDADES = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const SCHEDULE_START_HOUR = 7;
const SCHEDULE_END_HOUR = 22;
const SCHEDULE_STEP_MINUTES = 30;

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatHourLabel = (date: Date): string => {
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${hour}:${minute}`;
};

const formatPriorityLabel = (prioridade: string | undefined) => {
  const labels: Record<string, string> = {
    baixa: 'Baixa',
    normal: 'Normal',
    alta: 'Alta',
    urgente: 'Urgente',
  };

  return labels[prioridade || 'normal'] || 'Normal';
};

const getPriorityColor = (prioridade: string | undefined): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  switch (prioridade) {
    case 'baixa':
      return 'success';
    case 'normal':
      return 'info';
    case 'alta':
      return 'warning';
    case 'urgente':
      return 'error';
    default:
      return 'default';
  }
};

const getPriorityTextColor = (prioridade: string | undefined) => {
  switch (prioridade) {
    case 'baixa':
      return 'success.main';
    case 'normal':
      return 'info.main';
    case 'alta':
      return 'warning.main';
    case 'urgente':
      return 'error.main';
    default:
      return 'text.primary';
  }
};

const ChamadoForm: React.FC<ChamadoFormProps> = ({
  open,
  onClose,
  onSuccess,
  clientId,
  ticketId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(clientId || null);
  const [selectedClient, setSelectedClient] = useState<Client | undefined>(undefined);
  const [selectedContractId, setSelectedContractId] = useState<number | undefined>(undefined);
  const [selectedScheduleTime, setSelectedScheduleTime] = useState<string>('');
  const [scheduleTickets, setScheduleTickets] = useState<Ticket[]>([]);

  const [formData, setFormData] = useState<ChamadoFormData>({
    chamado_assunto_id: null,
    descricao: '',
    prioridade: 'normal',
    quem_solicitou: '',
    contrato_id: '',
    contrato_servico_id: '',
    data_agendamento: null,
    atribuido_para: null,
  });

  const loadTicketData = async (id: number) => {
    try {
      setLoading(true);
      const response = await ticketService.getById(id);
      if (response.success && response.data) {
        const ticket = response.data;
        setSelectedClientId(ticket.cliente_id);
        setSelectedContractId(ticket.contrato_id);

        setFormData({
          chamado_assunto_id: ticket.chamado_assunto_id || null,
          descricao: ticket.descricao || '',
          prioridade: ticket.prioridade || 'normal',
          quem_solicitou: ticket.quem_solicitou || '',
          contrato_id: ticket.contrato_id || '',
          contrato_servico_id: ticket.contrato_servico_id || '',
          data_agendamento: ticket.data_agendamento ? new Date(ticket.data_agendamento) : null,
          atribuido_para: ticket.atribuido_para || null,
        });
      }
    } catch (error: any) {
      console.error('Erro ao carregar os dados do chamado:', error);
      toast.error('Erro ao carregar dados do chamado.');
    } finally {
      setLoading(false);
    }
  };

  // Inicializar cliente se passado como prop ou carregar as infos do chamado
  useEffect(() => {
    if (open) {
      if (ticketId) {
        loadTicketData(ticketId);
      } else {
        if (clientId) {
          setSelectedClientId(clientId);
        } else {
          setSelectedClientId(null);
        }
        // Limpar dados ao abrir
        setFormData({
          chamado_assunto_id: null,
          descricao: '',
          prioridade: 'normal',
          quem_solicitou: '',
          contrato_id: '',
          contrato_servico_id: '',
          data_agendamento: null,
          atribuido_para: null,
        });
      }
    }
  }, [open, clientId, ticketId]);


  const handleChange = (field: keyof ChamadoFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      setSelectedScheduleTime('');
      handleChange('data_agendamento', null);
      return;
    }

    const nextDate = new Date(date);
    if (selectedScheduleTime) {
      const [hours, minutes] = selectedScheduleTime.split(':').map(Number);
      nextDate.setHours(hours || 0, minutes || 0, 0, 0);
    }

    handleChange('data_agendamento', nextDate);
  };

  const handleScheduleTimeChange = (time: string) => {
    setSelectedScheduleTime(time);
    if (!formData.data_agendamento) return;

    const [hours, minutes] = time.split(':').map(Number);
    const nextDate = new Date(formData.data_agendamento);
    nextDate.setHours(hours || 0, minutes || 0, 0, 0);
    handleChange('data_agendamento', nextDate);
  };

  const handleClientChange = (clientId: number | null, client?: Client) => {
    setSelectedClientId(clientId);
    setSelectedClient(client || undefined);
    // Limpar serviços quando mudar cliente
    handleChange('contrato_servico_id', '');
    handleChange('contrato_id', '');
    setSelectedContractId(undefined);
  };

  const handleServicoChange = (servicoId: number | null, servico?: ContratoServicos, contractId?: number) => {
    if (!servicoId || !servico) {
      handleChange('contrato_servico_id', '');
      handleChange('contrato_id', '');
      setSelectedContractId(undefined);
      return;
    }

    handleChange('contrato_servico_id', servicoId.toString());
    if (contractId) {
      handleChange('contrato_id', contractId);
      setSelectedContractId(contractId);
    }
  };

  useEffect(() => {
    if (!formData.data_agendamento) {
      setScheduleTickets([]);
      setSelectedScheduleTime('');
      return;
    }

    const selectedDate = new Date(formData.data_agendamento);
    const selectedDateKey = toDateKey(selectedDate);

    const loadScheduleTickets = async () => {
      try {
        const response = await ticketService.getAll({
          limit: 1000,
          filters: {
            status: 'todos',
            data_inicio: selectedDateKey,
            data_fim: selectedDateKey,
          },
        });

        if (response.success && response.data) {
          setScheduleTickets(response.data);
        } else {
          setScheduleTickets([]);
        }
      } catch (error) {
        console.error('Erro ao carregar agenda do dia:', error);
        setScheduleTickets([]);
      }
    };

    loadScheduleTickets();
  }, [formData.data_agendamento]);

  useEffect(() => {
    if (!formData.data_agendamento) {
      setSelectedScheduleTime('');
      return;
    }

    const hour = String(formData.data_agendamento.getHours()).padStart(2, '0');
    const minute = String(formData.data_agendamento.getMinutes()).padStart(2, '0');
    const nextTime = `${hour}:${minute}`;

    if (nextTime !== '00:00') {
      setSelectedScheduleTime(nextTime);
    } else {
      setSelectedScheduleTime('');
    }
  }, [formData.data_agendamento]);

  const scheduleOptions = React.useMemo(() => {
    if (!formData.data_agendamento) {
      return [] as Array<{
        value: string;
        disabled: boolean;
        tickets: Array<{
          id: number;
          priority: string | undefined;
          client: string;
          tecnico?: string;
        }>;
      }>;
    }

    const selectedDate = new Date(formData.data_agendamento);
    const selectedDateKey = toDateKey(selectedDate);
    const relevantTickets = scheduleTickets.filter((ticket) => {
      if (!ticket.data_agendamento) return false;
      const ticketDate = new Date(ticket.data_agendamento);
      if (toDateKey(ticketDate) !== selectedDateKey) return false;

      if (formData.atribuido_para) {
        return ticket.atribuido_para === formData.atribuido_para;
      }

      return true;
    });

    const priorityOrder: Record<string, number> = {
      urgente: 4,
      alta: 3,
      normal: 2,
      baixa: 1,
    };

    const slots: Array<{
      value: string;
      disabled: boolean;
      tickets: Array<{
        id: number;
        priority: string | undefined;
        client: string;
        tecnico?: string;
      }>;
    }> = [];

    for (let hour = SCHEDULE_START_HOUR; hour <= SCHEDULE_END_HOUR; hour += 1) {
      for (let minute = 0; minute < 60; minute += SCHEDULE_STEP_MINUTES) {
        const timeValue = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const matchingTickets = relevantTickets.filter((ticket) => {
          if (!ticket.data_agendamento) return false;
          const ticketDate = new Date(ticket.data_agendamento);
          return ticketDate.getHours() === hour && ticketDate.getMinutes() === minute;
        });

        const sortedMatchingTickets = [...matchingTickets].sort((a, b) => {
          const aPriority = priorityOrder[a.prioridade || 'normal'] ?? 0;
          const bPriority = priorityOrder[b.prioridade || 'normal'] ?? 0;

          if (bPriority !== aPriority) {
            return bPriority - aPriority;
          }

          return (a.id ?? 0) - (b.id ?? 0);
        });

        slots.push({
          value: timeValue,
          disabled: Boolean(formData.atribuido_para) && matchingTickets.length > 0,
          tickets: sortedMatchingTickets.map((ticket) => ({
            id: ticket.id,
            priority: ticket.prioridade,
            client: ticket.cliente?.nome || 'Cliente',
            tecnico: ticket.atribuido_para_funcionario?.nome,
          })),
        });
      }
    }

    return slots;
  }, [formData.data_agendamento, formData.atribuido_para, scheduleTickets]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validações
    if (formData.chamado_assunto_id == null) {
      setError('Assunto de chamado é obrigatório');
      return;
    }

    if (!formData.descricao || formData.descricao.trim().length < 10) {
      setError('Descrição deve ter no mínimo 10 caracteres');
      return;
    }

    if (!selectedClientId) {
      setError('Cliente é obrigatório');
      return;
    }

    if (!formData.contrato_servico_id) {
      setError('Serviço é obrigatório');
      return;
    }

    if (!formData.contrato_id) {
      setError('Erro: Contrato não identificado');
      return;
    }

    if (formData.data_agendamento && formData.atribuido_para) {
      const selectedDate = new Date(formData.data_agendamento);
      const selectedDateKey = toDateKey(selectedDate);
      const hasConflict = scheduleTickets.some((ticket) => {
        if (!ticket.data_agendamento || ticket.id === ticketId) return false;
        const ticketDate = new Date(ticket.data_agendamento);
        if (toDateKey(ticketDate) !== selectedDateKey) return false;
        if (ticket.atribuido_para !== formData.atribuido_para) return false;
        return ticketDate.getHours() === selectedDate.getHours() && ticketDate.getMinutes() === selectedDate.getMinutes();
      });

      if (hasConflict) {
        const hora = formatHourLabel(selectedDate);
        setError(`O técnico já tem outro chamado agendado para ${selectedDate.toLocaleDateString('pt-BR')} às ${hora}. Escolha outro horário.`);
        return;
      }
    }

    try {
      setLoading(true);

      if (ticketId) {
        // Atualizar
        const updateData = {
          cliente_id: selectedClientId || 0,
          contrato_id: Number(formData.contrato_id),
          chamado_assunto_id: formData.chamado_assunto_id ?? undefined,
          descricao: formData.descricao,
          prioridade: formData.prioridade,
          contrato_servico_id: formData.contrato_servico_id ? Number(formData.contrato_servico_id) : undefined,
          quem_solicitou: formData.quem_solicitou || undefined,
          data_agendamento: formData.data_agendamento || undefined,
        };
        const response = await ticketService.update(ticketId, updateData);
        if (response.success) {
          toast.success('Chamado atualizado com sucesso!');
          handleClose();
          if (onSuccess) {
            onSuccess();
          }
        } else {
          setError(response.message || 'Erro ao atualizar chamado');
        }
      } else {
        // Criar
        const ticketData = {
          cliente_id: selectedClientId,
          contrato_id: Number(formData.contrato_id),
          contrato_servico_id: formData.contrato_servico_id ? Number(formData.contrato_servico_id) : undefined,
          chamado_assunto_id: formData.chamado_assunto_id ?? undefined,
          descricao: formData.descricao,
          prioridade: formData.prioridade,
          quem_solicitou: formData.quem_solicitou || undefined,
          data_agendamento: formData.data_agendamento || undefined,
          atribuido_para: formData.atribuido_para || undefined,
        };

        const response = await ticketService.create(ticketData);

        if (response.success) {
          toast.success('Chamado criado com sucesso!');
          handleClose();
          if (onSuccess) {
            onSuccess();
          }
        } else {
          setError(response.message || 'Erro ao criar chamado');
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar chamado:', error);
      setError(error.response?.data?.message || 'Erro ao salvar chamado');
      toast.error('Erro ao salvar chamado');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        chamado_assunto_id: null,
        descricao: '',
        prioridade: 'normal',
        quem_solicitou: '',
        contrato_id: '',
        contrato_servico_id: '',
        data_agendamento: null,
        atribuido_para: null,
      });
      if (!clientId) {
        setSelectedClientId(null);
        setSelectedClient(undefined);
      }
      setSelectedContractId(undefined);
      setError(null);
      onClose();
    }
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SupportIcon color="primary" />
            <Typography variant="h6" component="span">
              {ticketId ? 'Editar Chamado' : 'Novo Chamado'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Seleção de Cliente */}
            {!clientId && !ticketId && (
              <Grid item xs={12}>
                <ClientSelect
                  value={selectedClientId}
                  onChange={handleClientChange}
                  label="Cliente"
                  placeholder="Selecione o cliente"
                  required
                  disabled={loading}
                  size="small"
                  error={!!error && !selectedClientId}
                  helperText={!selectedClientId ? 'Selecione um cliente para continuar' : ''}
                />
              </Grid>
            )}

            {/* Informação do cliente selecionado (se passado como prop ou editando) */}
            {(clientId || ticketId) && selectedClient && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Cliente: {selectedClient.nome} - {selectedClient.cpf_cnpj}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Assunto do chamado */}
            <Grid item xs={12} sm={4}>
              <ChamadoAssuntoSelect
                value={formData.chamado_assunto_id}
                onChange={(assuntoId, assunto) => {
                  handleChange('chamado_assunto_id', assuntoId);
                }}
                label="Assunto do Chamado"
                required
                disabled={loading}
                size="small"
                allowClear={false}
                error={!!error && !formData.chamado_assunto_id}
                helperText={!formData.chamado_assunto_id ? 'Selecione o assunto do chamado' : ''}
              />
            </Grid>

            {/* Prioridade */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={formData.prioridade}
                  label="Prioridade"
                  onChange={(e) => handleChange('prioridade', e.target.value)}
                  disabled={loading}
                  size="small"
                >
                  {PRIORIDADES.map((prioridade) => (
                    <MenuItem key={prioridade.value} value={prioridade.value}>
                      {prioridade.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Quem Solicitou */}
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Quem Solicitou (Opcional)"
                value={formData.quem_solicitou}
                onChange={(e) => handleChange('quem_solicitou', e.target.value)}
                disabled={loading}
                placeholder="Nome da pessoa que solicitou"
                size="small"
              />
            </Grid>

            {/* Serviço */}
            <Grid item xs={12} sm={6}>
              <ContractServicosSelect
                value={formData.contrato_servico_id}
                onChange={handleServicoChange}
                clientId={selectedClientId || undefined}
                label="Serviço"
                required
                disabled={loading || !selectedClientId}
                error={!!error && !formData.contrato_servico_id}
                helperText={!selectedClientId ? 'Selecione um cliente primeiro' : ''}
                size="small"
                onlyActive={false}
                showContractTitle={true}
              />
            </Grid>

            {/* Atribuir para (só na criação, na edição é via tela view) */}
            {!ticketId && (
              <Grid item xs={12} sm={6}>
                <FuncionariosSelect
                  value={formData.atribuido_para}
                  onChange={(value) => handleChange('atribuido_para', value)}
                  label="Atribuir para"
                  placeholder="Selecione"
                  disabled={loading}
                  size="small"
                  allowClear
                />
              </Grid>
            )}

            {/* Descrição */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Descrição"
                multiline
                rows={4}
                value={formData.descricao}
                onChange={(e) => handleChange('descricao', e.target.value)}
                disabled={loading}
                helperText={`Mínimo 10 caracteres (${formData.descricao.length})`}
                error={formData.descricao.length > 0 && formData.descricao.length < 10}
                size="small"
              />
            </Grid>

            {/* Data de Agendamento */}
            <Grid item xs={12} sm={6}>
              <Box>
                <SysDateTime
                  label="Data de Agendamento"
                  value={formData.data_agendamento}
                  onChange={handleDateChange}
                  type="data"
                  placeholder="Selecione a data do agendamento (Opcional)"
                  disabled={loading}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="schedule-time-label" shrink>Horário</InputLabel>
                <Select
                  labelId="schedule-time-label"
                  value={selectedScheduleTime || ''}
                  label="Horário"
                  onChange={(e) => handleScheduleTimeChange(e.target.value)}
                  disabled={loading || !formData.data_agendamento}
                  displayEmpty
                  notched
                  sx={{ borderRadius: 1 }}
                >
                  {!formData.data_agendamento && (
                    <MenuItem value="" disabled>
                      Selecione a data primeiro
                    </MenuItem>
                  )}
                  {formData.data_agendamento && scheduleOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      Sem horários disponíveis para esta data
                    </MenuItem>
                  )}
                  {formData.data_agendamento && scheduleOptions.length > 0 && (
                    <MenuItem value="" disabled>
                      Selecione o horário
                    </MenuItem>
                  )}
                  {scheduleOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      sx={{ py: 1, '&.Mui-selected': { backgroundColor: 'action.hover' }, '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: option.tickets.length > 0 ? 0.5 : 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 48, color: 'text.primary' }}>
                            {option.value}
                          </Typography>
                          {option.tickets.length === 0 && (
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                              Disponível
                            </Typography>
                          )}
                        </Box>

                        {option.tickets.length > 0 && option.tickets.map((ticket) => (
                          <Box
                            key={`${option.value}-${ticket.id}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              minWidth: 0,
                              py: 0.15,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: getPriorityTextColor(ticket.priority),
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: getPriorityTextColor(ticket.priority),
                                fontWeight: 700,
                                lineHeight: 1.2,
                              }}
                            >
                              {formatPriorityLabel(ticket.priority)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.2 }}>
                              #{ticket.id}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.primary',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 170,
                              }}
                            >
                              {ticket.client}
                            </Typography>
                            {ticket.tecnico && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 'auto', color: 'text.secondary' }}>
                                <EngineeringIcon sx={{ fontSize: 12 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.62rem', lineHeight: 1.2, color: 'text.secondary' }}>
                                  {ticket.tecnico}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            startIcon={<CloseIcon />}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !selectedClientId || formData.chamado_assunto_id == null || !formData.descricao || !formData.contrato_servico_id}
            startIcon={loading ? <CircularProgress size={16} /> : <SupportIcon />}
          >
            {loading ? (ticketId ? 'Atualizando...' : 'Criando...') : (ticketId ? 'Atualizar Chamado' : 'Criar Chamado')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChamadoForm;

